import React, { FC, useState } from "react";
import api from "api";
import { Features } from "providers/auth/types";
import { useAuth } from "providers/auth";
import { useQuery } from "@tanstack/react-query";
import { SelectOption, View } from "../../types";
import { MovedOutScreenView } from "./slices/moved-out";
import { UpdateLeaseDetailsView } from "./slices/new-lease-end-date";
import { useNavigate, useParams } from "react-router-dom";
import { Redirect } from "common/Redirect";
import { Loader } from "shared/components/Loader";
import { CustomerIssue } from "types/issue";
import { SuccessScreenView } from "../../components/SuccessScreen";
import { SelectScreenView } from "views/rent-reporting-id-issues-m2m-lease-end/components/SelectScreenView";

type Props = {
  issue?: CustomerIssue;
};

const handleContinue = (
  selectedOption: SelectOption,
  setView: (view: View) => void
) => {
  if (selectedOption === SelectOption.MonthToMonth) setView(View.SuccessView);

  if (selectedOption === SelectOption.MovedOut) setView(View.MovedOutView);

  if (selectedOption === SelectOption.NewLeaseEndDate)
    setView(View.LeaseDetailsView);
};

export const M2MPaymentVerificationView: FC<Props> = ({ issue }) => {
  const { partner } = useAuth();
  const { id } = useParams();

  const navigate = useNavigate();
  const {
    data: enrollment,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["LATEST_ENROLLMENT"],
    queryFn: () =>
      api.getDashboard().then((res) => {
        return res.data.enrollments.find(
          (enrollment) => enrollment.id === Number(id)
        );
      }),
    enabled: Boolean(
      partner?.features?.includes(Features.ENABLE_SDK_RENT_REPORTING)
    )
  });
  const [view, setView] = useState<View>(View.SelectView);
  const [selectedOption, setSelectedOption] = useState<SelectOption>(
    SelectOption.MonthToMonth
  );

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  if (isError || !enrollment)
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-content-1000 font-medium text-base">
          Error loading enrollment data
        </p>
      </div>
    );
  if (view === View.MovedOutView)
    return <MovedOutScreenView setView={setView} issue={issue} />;
  if (view === View.LeaseDetailsView)
    return (
      <UpdateLeaseDetailsView
        setView={setView}
        monthlyRentAmount={enrollment.monthly_rent_amount}
      />
    );
  if (view === View.SuccessView)
    return (
      <SuccessScreenView
        title="Thanks!"
        subtext1="Please click below to verify your missing payments"
        buttonTitle="Verify payments"
        onButtonClick={() => navigate("/rent_reporting/issues/verify-rent")}
      />
    );
  if (view === View.SelectView) {
    const optionsMap = [
      {
        id: SelectOption.MonthToMonth,
        title: "Lease is still month-to-month"
      },
      {
        id: SelectOption.MovedOut,
        title: "I already moved out"
      },
      {
        id: SelectOption.NewLeaseEndDate,
        title: "I have a new lease end date"
      }
    ];
    return (
      <SelectScreenView
        title="We are unable to detect one or more rental payments"
        subtitle={`Your lease at ${enrollment.address?.address1} is set as a month-to-month lease`}
        options={optionsMap}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        handleContinue={() => handleContinue(selectedOption, setView)}
      />
    );
  }

  return <Redirect href="/hub?with_intercom=true" />;
};
